import React, { useState, useEffect, useCallback } from 'react';
import {
    FaGraduationCap,
    FaSync,
    FaCheck,
    FaTimes,
    FaSpinner,
    FaExclamationTriangle,
    FaCheckCircle,
    FaCalendarAlt,
    FaFileAlt,
    FaExternalLinkAlt,
    FaCopy,
    FaUndo,
    FaInfoCircle,
} from 'react-icons/fa';
import {
    stageTrainingReview,
    approveTrainingReview,
    getTrainingRevertState,
    revertTrainingReview,
    getOnboardingCrossDivisionReviewScope,
} from '../../services/onboardingService';
import CrossDivisionScopeModal from './CrossDivisionScopeModal';

const ERROR_LABELS = {
    'TRAINING_SOURCE_CHANGED': 'The API training data changed. Load training again and review it.',
    'TRAINING_COURSE_UNAVAILABLE': 'A selected course already exists or is no longer available. Load training again.',
    'TRAINING_REVIEW_CHANGED': 'A newer training review exists. Load training again.',
    'TRAINING_RECORD_CHANGED_AFTER_APPROVAL': 'An imported training record was edited or removed after approval. Revert was blocked; no records were deleted.',
    'NEWER_TRAINING_APPROVAL_EXISTS': 'A newer training approval exists. Refresh revert status first.',
    'EMPLOYEE_EMAIL_MISMATCH': 'The employee email no longer matches the candidate. Review the identity match.',
    'AMBIGUOUS_EMPLOYEE_MATCH': 'More than one employee has the candidate email. Resolve the match first.',
    'TRAINING_EXPIRY_REQUIRED': 'Confirm a valid expiry date for every selected course.',
    'ASSIGN_CERTIFICATE_TO_EACH_COURSE': 'Assign at least one certificate to each selected course.',
};

const mapErrorMessage = (err) => {
    if (!err) return '';
    const details = err.details || {};
    const reason = details.reason || err.code || '';
    if (ERROR_LABELS[reason]) return ERROR_LABELS[reason];
    return err.message || String(err);
};

const isDateExpired = (isoDate) => {
    if (!isoDate) return false;
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return d < now;
};

const TrainingReview = ({
    serverID,
    purgatoryID,
    employeeID,
    disabled = false,
}) => {
    const [busy, setBusy] = useState(false);
    const [proposalID, setProposalID] = useState(null);
    const [courses, setCourses] = useState([]);
    const [certificates, setCertificates] = useState([]);
    const [warnings, setWarnings] = useState([]);
    const [selectedCourses, setSelectedCourses] = useState(new Set());
    const [assignedFiles, setAssignedFiles] = useState({}); // courseKey -> Set<certKey>
    const [expiryDates, setExpiryDates] = useState({}); // courseKey -> YYYY-MM-DD
    const [revertID, setRevertID] = useState(null);
    const [revertCount, setRevertCount] = useState(0);
    const [feedback, setFeedback] = useState(null); // { text, isError }
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRevertModal, setShowRevertModal] = useState(false);
    const [copiedKey, setCopiedKey] = useState(null);
    const [crossDivisionScope, setCrossDivisionScope] = useState(null);
    const [crossDivisionOp, setCrossDivisionOp] = useState('approve'); // 'approve' | 'revert'
    const [checkingScope, setCheckingScope] = useState(false);

    // Check recovery/revert availability
    const checkRecovery = useCallback(async () => {
        if (!serverID || !purgatoryID) return;
        try {
            const res = await getTrainingRevertState(serverID, purgatoryID);
            if (res?.available === true && res?.proposalID) {
                setRevertID(res.proposalID);
                setRevertCount(Number(res.created || 0));
            } else {
                setRevertID(null);
                setRevertCount(0);
            }
        } catch (err) {
            console.error('Failed to check training revert state:', err);
        }
    }, [serverID, purgatoryID]);

    useEffect(() => {
        checkRecovery();
    }, [checkRecovery]);

    // Reset on candidate switch
    useEffect(() => {
        setProposalID(null);
        setCourses([]);
        setCertificates([]);
        setWarnings([]);
        setSelectedCourses(new Set());
        setAssignedFiles({});
        setExpiryDates({});
        setFeedback(null);
        setCrossDivisionScope(null);
        setShowApproveModal(false);
        setShowRevertModal(false);
    }, [serverID, purgatoryID, employeeID]);

    const handleLoadTraining = async () => {
        if (busy || disabled) return;
        setBusy(true);
        setFeedback(null);

        try {
            const res = await stageTrainingReview(serverID, purgatoryID);
            if (employeeID && res?.employeeID && res.employeeID !== employeeID) {
                throw new Error('Employee match changed. Reload the page.');
            }

            const plan = res?.plan || {};
            setProposalID(res?.proposalID || null);
            setCourses(plan.courses || []);
            setCertificates(plan.certificates || []);
            setWarnings(plan.warnings || []);
            setSelectedCourses(new Set());
            setAssignedFiles({});
            setExpiryDates({});
            setFeedback({
                text: 'Training staged successfully. No records have been created yet.',
                isError: false,
            });

            await checkRecovery();
        } catch (err) {
            console.error('Failed to stage training review:', err);
            setFeedback({ text: mapErrorMessage(err), isError: true });
        } finally {
            setBusy(false);
        }
    };

    const handleToggleCourse = (courseKey) => {
        if (busy) return;
        setSelectedCourses((prev) => {
            const next = new Set(prev);
            if (next.has(courseKey)) {
                next.delete(courseKey);
            } else {
                next.add(courseKey);
                // Initialize assignedFiles set if absent
                if (!assignedFiles[courseKey]) {
                    setAssignedFiles((af) => ({ ...af, [courseKey]: new Set() }));
                }
            }
            return next;
        });
    };

    const handleToggleCertificate = (courseKey, certKey) => {
        if (busy) return;
        setAssignedFiles((prev) => {
            const currentSet = new Set(prev[courseKey] || []);
            if (currentSet.has(certKey)) {
                currentSet.delete(certKey);
            } else {
                currentSet.add(certKey);
            }
            return { ...prev, [courseKey]: currentSet };
        });
    };

    const handleDateChange = (courseKey, val) => {
        setExpiryDates((prev) => ({ ...prev, [courseKey]: val }));
    };

    const handleCopyLink = async (key, url) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey(null), 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    };

    const handlePreApprove = async () => {
        if (busy || checkingScope || !proposalID || selectedCourses.size === 0) return;

        // Validation check: Each selected course must have at least 1 certificate and an expiry date
        const selectedList = courses.filter((c) => selectedCourses.has(c.key));
        const missingAssignments = selectedList.some((c) => {
            const files = assignedFiles[c.key];
            const date = expiryDates[c.key];
            return !files || files.size === 0 || !date;
        });

        if (missingAssignments) {
            setFeedback({
                text: 'Please assign at least one certificate and confirm an expiry date for each selected course.',
                isError: true,
            });
            return;
        }

        setCheckingScope(true);
        setFeedback(null);

        try {
            const scopeRes = await getOnboardingCrossDivisionReviewScope({
                serverID,
                purgatoryID,
                proposalID,
                kind: 'training',
                operation: 'approve',
            });

            if (scopeRes?.success === true && (scopeRes.affectedDivisions?.length > 1 || scopeRes.groupID)) {
                setCrossDivisionScope(scopeRes);
                setCrossDivisionOp('approve');
            } else {
                setShowApproveModal(true);
            }
        } catch (scopeErr) {
            console.warn('Cross-division training approve scope check fallback:', scopeErr);
            setShowApproveModal(true);
        } finally {
            setCheckingScope(false);
        }
    };

    const handleConfirmApprove = async (crossDivisionConfirmation = '') => {
        if (busy || !proposalID) return;
        setBusy(true);
        setFeedback(null);

        const selectedList = courses.filter((c) => selectedCourses.has(c.key));
        const assignments = selectedList.map((c) => ({
            courseKey: c.key,
            certificateKeys: Array.from(assignedFiles[c.key] || []),
            expiryDate: expiryDates[c.key],
        }));

        try {
            const res = await approveTrainingReview(
                serverID,
                purgatoryID,
                proposalID,
                assignments,
                typeof crossDivisionConfirmation === 'string' ? crossDivisionConfirmation : ''
            );

            const msg = res?.alreadyApproved === true
                ? 'This training approval was already completed.'
                : `${res?.created || selectedList.length} training record(s) created in employee profile.`;

            setFeedback({ text: msg, isError: false });
            setShowApproveModal(false);
            setCrossDivisionScope(null);
            setProposalID(null);
            setSelectedCourses(new Set());
            setCourses([]);
            setCertificates([]);
            setWarnings([]);

            await checkRecovery();
        } catch (err) {
            console.error('Failed to approve training:', err);
            setFeedback({ text: mapErrorMessage(err), isError: true });
        } finally {
            setBusy(false);
        }
    };

    const handlePreRevert = async () => {
        if (busy || checkingScope || !revertID) return;
        setCheckingScope(true);
        setFeedback(null);

        try {
            const scopeRes = await getOnboardingCrossDivisionReviewScope({
                serverID,
                purgatoryID,
                proposalID: revertID,
                kind: 'training',
                operation: 'revert',
            });

            if (scopeRes?.success === true && (scopeRes.affectedDivisions?.length > 1 || scopeRes.groupID)) {
                setCrossDivisionScope(scopeRes);
                setCrossDivisionOp('revert');
            } else {
                setShowRevertModal(true);
            }
        } catch (scopeErr) {
            console.warn('Cross-division training revert scope check fallback:', scopeErr);
            setShowRevertModal(true);
        } finally {
            setCheckingScope(false);
        }
    };

    const handleConfirmRevert = async (crossDivisionConfirmation = '') => {
        if (busy || !revertID) return;
        setBusy(true);
        setFeedback(null);

        try {
            const res = await revertTrainingReview(
                serverID,
                purgatoryID,
                revertID,
                typeof crossDivisionConfirmation === 'string' ? crossDivisionConfirmation : ''
            );
            const msg = res?.alreadyReverted === true
                ? 'This import was already reverted.'
                : `${res?.removed || revertCount} imported training record(s) removed.`;

            setFeedback({ text: msg, isError: false });
            setShowRevertModal(false);
            setCrossDivisionScope(null);
            setProposalID(null);
            setSelectedCourses(new Set());
            setCourses([]);
            setCertificates([]);
            setWarnings([]);
            setRevertID(null);
            setRevertCount(0);

            await checkRecovery();
        } catch (err) {
            console.error('Failed to revert training:', err);
            setFeedback({ text: mapErrorMessage(err), isError: true });
        } finally {
            setBusy(false);
        }
    };

    const selectedList = courses.filter((c) => selectedCourses.has(c.key));

    return (
        <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #edf2f7',
            }}>
                <div>
                    <h3 style={{
                        margin: 0,
                        fontSize: '16px',
                        fontWeight: '800',
                        color: '#183D44',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        <FaGraduationCap style={{ color: '#319795' }} />
                        Training & Certificate Review
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#718096' }}>
                        Attach verified Onboarding360 certificates to training courses and confirm expiration dates.
                    </p>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    <button
                        type="button"
                        onClick={handleLoadTraining}
                        disabled={busy || disabled}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#ffffff',
                            color: '#319795',
                            border: '1px solid #b2f5ea',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: busy || disabled ? 'not-allowed' : 'pointer',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        }}
                    >
                        {busy ? (
                            <>
                                <FaSpinner className="fa-spin" />
                                <span>Working…</span>
                            </>
                        ) : (
                            <>
                                <FaSync />
                                <span>{proposalID ? 'Reload & Clear Selections' : 'Load Training for Review'}</span>
                            </>
                        )}
                    </button>

                    {revertID && (
                        <button
                            type="button"
                            onClick={handlePreRevert}
                            disabled={busy || checkingScope || disabled}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: '#fffaf0',
                                color: '#c05621',
                                border: '1px solid #fbd38d',
                                padding: '8px 14px',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: busy || checkingScope || disabled ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {checkingScope && crossDivisionOp === 'revert' ? (
                                <>
                                    <FaSpinner className="fa-spin" />
                                    <span>Checking Scope…</span>
                                </>
                            ) : (
                                <>
                                    <FaUndo />
                                    <span>Revert Last Training Import ({revertCount})</span>
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* General Advice */}
            <div style={{
                backgroundColor: '#f7fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                padding: '10px 14px',
                marginBottom: '14px',
                fontSize: '12.5px',
                color: '#4a5568',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
            }}>
                <FaInfoCircle style={{ color: '#3182ce', flexShrink: 0 }} />
                <span>
                    Certificates and expiry dates must be verified manually by an administrator. Nothing is selected automatically.
                </span>
            </div>

            {/* Feedback Banners */}
            {feedback && (
                <div style={{
                    backgroundColor: feedback.isError ? '#fff5f5' : '#f0fff4',
                    border: `1px solid ${feedback.isError ? '#fed7d7' : '#c6f6d5'}`,
                    borderRadius: '6px',
                    padding: '10px 14px',
                    marginBottom: '14px',
                    color: feedback.isError ? '#c53030' : '#276749',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                }}>
                    {feedback.isError ? (
                        <FaExclamationTriangle style={{ marginTop: '2px', flexShrink: 0 }} />
                    ) : (
                        <FaCheckCircle style={{ marginTop: '2px', flexShrink: 0 }} />
                    )}
                    <span>{feedback.text}</span>
                </div>
            )}

            {/* Warnings from Backend Staging */}
            {warnings.length > 0 && (
                <div style={{
                    backgroundColor: '#fffaf0',
                    border: '1px solid #feebc8',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    marginBottom: '14px',
                    fontSize: '12.5px',
                    color: '#c05621',
                }}>
                    <strong>Staging Warnings:</strong>
                    <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                        {warnings.map((w, idx) => (
                            <li key={idx}>{w}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Staged Content */}
            {proposalID ? (
                <div>
                    {/* Shelf 1: Certificates Available */}
                    <div style={{ marginBottom: '18px' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '8px',
                        }}>
                            <h4 style={{ margin: 0, fontSize: '13.5px', fontWeight: '700', color: '#2d3748' }}>
                                Uploaded Certificates ({certificates.length})
                            </h4>
                            <span style={{ fontSize: '12px', color: '#718096' }}>
                                Files remain securely hosted on Onboarding360
                            </span>
                        </div>

                        {certificates.length === 0 ? (
                            <div style={{
                                padding: '12px',
                                backgroundColor: '#fff5f5',
                                border: '1px solid #fed7d7',
                                borderRadius: '6px',
                                fontSize: '13px',
                                color: '#c53030',
                            }}>
                                No usable certificate links found in source payload. Training cannot be approved without verified files.
                            </div>
                        ) : (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                gap: '10px',
                            }}>
                                {certificates.map((cert) => (
                                    <div key={cert.key} style={{
                                        border: '1px solid #e2e8f0',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '8px',
                                        padding: '10px 12px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                    }}>
                                        <div>
                                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#183D44' }}>
                                                {cert.label}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#718096', wordBreak: 'break-all' }}>
                                                {cert.name}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                            <a
                                                href={cert.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    fontSize: '11.5px',
                                                    color: '#2b6cb0',
                                                    textDecoration: 'none',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    padding: '3px 8px',
                                                    backgroundColor: '#ebf8ff',
                                                    borderRadius: '4px',
                                                }}
                                            >
                                                <FaExternalLinkAlt /> Open File
                                            </a>
                                            <button
                                                type="button"
                                                onClick={() => handleCopyLink(cert.key, cert.url)}
                                                style={{
                                                    fontSize: '11.5px',
                                                    color: copiedKey === cert.key ? '#276749' : '#4a5568',
                                                    backgroundColor: copiedKey === cert.key ? '#c6f6d5' : '#edf2f7',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '3px 8px',
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                }}
                                            >
                                                <FaCopy /> {copiedKey === cert.key ? 'Copied' : 'Copy Link'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Shelf 2: Courses */}
                    <div style={{ marginBottom: '18px' }}>
                        <div style={{ marginBottom: '10px' }}>
                            <h4 style={{ margin: 0, fontSize: '13.5px', fontWeight: '700', color: '#2d3748' }}>
                                Available Courses ({courses.length})
                            </h4>
                            <p style={{ margin: '2px 0 0 0', fontSize: '12.5px', color: '#718096' }}>
                                A certificate covering multiple courses can be assigned to each respective course.
                            </p>
                        </div>

                        {courses.map((course) => {
                            const isSelected = selectedCourses.has(course.key);
                            const canCreate = course.canCreate === true;
                            const filesForThisCourse = assignedFiles[course.key] || new Set();
                            const expiryVal = expiryDates[course.key] || '';
                            const isExpired = isDateExpired(expiryVal);

                            return (
                                <div key={course.key} style={{
                                    border: `1px solid ${isSelected ? '#b2f5ea' : '#e2e8f0'}`,
                                    backgroundColor: isSelected ? '#f0fdf4' : '#ffffff',
                                    borderRadius: '8px',
                                    padding: '14px',
                                    marginBottom: '10px',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                        <input
                                            type="checkbox"
                                            id={`course-${course.key}`}
                                            checked={isSelected}
                                            disabled={busy || !canCreate || certificates.length === 0}
                                            onChange={() => handleToggleCourse(course.key)}
                                            style={{
                                                width: '18px',
                                                height: '18px',
                                                marginTop: '2px',
                                                cursor: canCreate && certificates.length > 0 ? 'pointer' : 'not-allowed',
                                            }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <label htmlFor={`course-${course.key}`} style={{
                                                fontSize: '14px',
                                                fontWeight: '700',
                                                color: canCreate ? '#183D44' : '#718096',
                                                cursor: canCreate && certificates.length > 0 ? 'pointer' : 'not-allowed',
                                            }}>
                                                {course.title}
                                            </label>
                                            {!canCreate && course.blockedReason && (
                                                <div style={{ fontSize: '12px', color: '#c53030', marginTop: '2px' }}>
                                                    {course.blockedReason}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Expanded Assignment Controls when selected */}
                                    {isSelected && (
                                        <div style={{
                                            marginTop: '12px',
                                            paddingLeft: '28px',
                                            borderTop: '1px dashed #cbd5e0',
                                            paddingTop: '10px',
                                        }}>
                                            {/* File Checkboxes */}
                                            <div style={{ marginBottom: '10px' }}>
                                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#4a5568', marginBottom: '6px' }}>
                                                    Assign Supporting Certificate(s):
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    {certificates.map((cert) => {
                                                        const isFileAssigned = filesForThisCourse.has(cert.key);
                                                        return (
                                                            <label key={cert.key} style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                fontSize: '12.5px',
                                                                color: '#2d3748',
                                                                cursor: 'pointer',
                                                            }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isFileAssigned}
                                                                    onChange={() => handleToggleCertificate(course.key, cert.key)}
                                                                    disabled={busy}
                                                                    style={{ width: '15px', height: '15px' }}
                                                                />
                                                                <span>{cert.label} ({cert.name})</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Expiry Date Input */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                <div>
                                                    <label style={{ fontSize: '12px', fontWeight: '700', color: '#4a5568', display: 'block', marginBottom: '4px' }}>
                                                        Confirmed Expiry Date:
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={expiryVal}
                                                        onChange={(e) => handleDateChange(course.key, e.target.value)}
                                                        disabled={busy}
                                                        style={{
                                                            padding: '6px 10px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #cbd5e0',
                                                            fontSize: '13px',
                                                        }}
                                                    />
                                                </div>
                                                {isExpired && (
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: '#c53030',
                                                        backgroundColor: '#fff5f5',
                                                        padding: '4px 10px',
                                                        borderRadius: '4px',
                                                        marginTop: '16px',
                                                    }}>
                                                        Expiry date is in the past. Record will be registered with an expired date.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Bottom Action Button */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        paddingTop: '12px',
                        borderTop: '1px solid #edf2f7',
                    }}>
                        <button
                            type="button"
                            onClick={handlePreApprove}
                            disabled={busy || checkingScope || disabled || selectedCourses.size === 0}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: '#319795',
                                color: '#ffffff',
                                border: 'none',
                                padding: '9px 20px',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: '700',
                                cursor: busy || checkingScope || disabled || selectedCourses.size === 0 ? 'not-allowed' : 'pointer',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            }}
                        >
                            {checkingScope && crossDivisionOp === 'approve' ? (
                                <>
                                    <FaSpinner className="fa-spin" />
                                    <span>Checking Scope…</span>
                                </>
                            ) : (
                                <>
                                    <FaCheck />
                                    <span>Review & Create {selectedCourses.size} Training Record(s)</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            ) : (
                <div style={{ fontSize: '12.5px', color: '#718096', fontStyle: 'italic' }}>
                    Click "Load Training for Review" to fetch certificates and courses staged from Onboarding360.
                </div>
            )}

            {/* Approval Confirmation Modal */}
            {showApproveModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        maxWidth: '560px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '14px',
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '16px',
                                fontWeight: '700',
                                color: '#183D44',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}>
                                <FaCheck /> Create {selectedList.length} training record(s)?
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowApproveModal(false)}
                                disabled={busy}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '16px',
                                    color: '#a0aec0',
                                    cursor: 'pointer',
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div style={{
                            overflowY: 'auto',
                            fontSize: '13px',
                            color: '#4a5568',
                            lineHeight: '1.5',
                            marginBottom: '18px',
                        }}>
                            <p style={{ margin: '0 0 10px 0' }}>
                                Confirm that selected certificates cover each course and that expiration dates have been checked:
                            </p>

                            <div style={{
                                backgroundColor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                padding: '10px 14px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                marginBottom: '12px',
                            }}>
                                {selectedList.map((c) => {
                                    const files = Array.from(assignedFiles[c.key] || [])
                                        .map((fk) => certificates.find((f) => f.key === fk)?.label)
                                        .filter(Boolean)
                                        .join(', ');
                                    const isExp = isDateExpired(expiryDates[c.key]);

                                    return (
                                        <div key={c.key} style={{ padding: '6px 0', borderBottom: '1px solid #edf2f7' }}>
                                            <div style={{ fontWeight: '700', color: '#183D44' }}>{c.title}</div>
                                            <div style={{ fontSize: '12px', color: '#718096' }}>
                                                Expiry: <strong>{expiryDates[c.key]}</strong> {isExp && <span style={{ color: '#c53030' }}>(Expired)</span>}
                                            </div>
                                            <div style={{ fontSize: '11.5px', color: '#4a5568' }}>
                                                Files: {files || 'None'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <p style={{ margin: 0, color: '#718096', fontSize: '12px' }}>
                                These records are added to this employee's training subcollection. Existing training and employee access remain unchanged.
                            </p>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px',
                            borderTop: '1px solid #edf2f7',
                            paddingTop: '14px',
                        }}>
                            <button
                                type="button"
                                onClick={() => setShowApproveModal(false)}
                                disabled={busy}
                                style={{
                                    backgroundColor: '#edf2f7',
                                    color: '#4a5568',
                                    border: '1px solid #cbd5e0',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: busy ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmApprove}
                                disabled={busy}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#319795',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: busy ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {busy ? (
                                    <>
                                        <FaSpinner className="fa-spin" />
                                        <span>Creating…</span>
                                    </>
                                ) : (
                                    <span>Confirm & Create</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Revert Confirmation Modal */}
            {showRevertModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        maxWidth: '520px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '14px',
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '16px',
                                fontWeight: '700',
                                color: '#742a2a',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}>
                                <FaUndo /> Revert last training import?
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowRevertModal(false)}
                                disabled={busy}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '16px',
                                    color: '#a0aec0',
                                    cursor: 'pointer',
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div style={{
                            fontSize: '13px',
                            color: '#4a5568',
                            lineHeight: '1.5',
                            marginBottom: '18px',
                        }}>
                            <p style={{ margin: '0 0 10px 0' }}>
                                Remove the <strong>{revertCount}</strong> training record(s) created by this approval?
                            </p>
                            <p style={{ margin: 0, color: '#718096', fontSize: '12px' }}>
                                If any imported record has been altered or updated since approval, the entire revert will be blocked. Other training records remain preserved.
                            </p>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px',
                        }}>
                            <button
                                type="button"
                                onClick={() => setShowRevertModal(false)}
                                disabled={busy}
                                style={{
                                    backgroundColor: '#edf2f7',
                                    color: '#4a5568',
                                    border: '1px solid #cbd5e0',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: busy ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmRevert}
                                disabled={busy}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#c53030',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: busy ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {busy ? (
                                    <>
                                        <FaSpinner className="fa-spin" />
                                        <span>Reverting…</span>
                                    </>
                                ) : (
                                    <span>Confirm Revert</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cross-Division Intelligence Scope Modal */}
            <CrossDivisionScopeModal
                isOpen={Boolean(crossDivisionScope)}
                scope={crossDivisionScope}
                kind="training"
                operation={crossDivisionOp}
                assignments={courses.filter((c) => selectedCourses.has(c.key)).map((c) => ({
                    courseKey: c.key,
                    certificateKeys: Array.from(assignedFiles[c.key] || []),
                    expiryDate: expiryDates[c.key],
                }))}
                onConfirm={(groupID) => {
                    if (crossDivisionOp === 'approve') {
                        handleConfirmApprove(groupID);
                    } else {
                        handleConfirmRevert(groupID);
                    }
                }}
                onCancel={() => setCrossDivisionScope(null)}
                busy={busy}
            />
        </div>
    );
};

export default TrainingReview;
